/**
 * The route table: one entry per screen. The sidebar, the route guard, the page title
 * and the contextual Help link are all derived from it.
 *
 * To add a screen (D3–D5): write the page in src/app/pages/<module>/, then point its
 * entry's `page` at it (lazy import) instead of `placeholder(...)`. Keep `perm` in step
 * with the API's permission names (lib/permissions.ts). See docs/sprints/D2.md.
 */
import {
  Activity,
  Archive,
  BadgeCheck,
  BookLock,
  Building2,
  ChartNoAxesColumn,
  CircleHelp,
  Clock3,
  FileBarChart,
  FileStack,
  FileText,
  Gauge,
  History,
  House,
  Inbox,
  FilePen,
  PenLine,
  Send,
  Signature,
  UserCheck,
  Workflow,
  Settings2,
  KeyRound,
  LifeBuoy,
  Link2,
  ListChecks,
  Library,
  Recycle,
  Scale,
  Search,
  Settings,
  ShieldHalf,
  FileX2,
  SlidersHorizontal,
  Timer,
  Trash2,
  Upload,
  Users,
  UsersRound,
  Wand2,
  type LucideIcon,
} from 'lucide-react';
import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import { P } from '../lib/permissions';
import { placeholder } from './pages/Placeholder';

export type GroupId = 'documents' | 'esign' | 'records' | 'admin' | 'platform';

export interface NavGroup {
  id: GroupId;
  label: string;
  icon: LucideIcon;
}

export const GROUPS: NavGroup[] = [
  { id: 'documents', label: 'Documents', icon: FileStack },
  { id: 'esign', label: 'e-Sign', icon: Signature },
  { id: 'records', label: 'Records', icon: Archive },
  { id: 'admin', label: 'Admin', icon: Settings },
  { id: 'platform', label: 'Platform', icon: Building2 },
];

/** Sidebar order: top-level links and groups. 'cabinets' is the special tree entry. */
export const NAV_ORDER: (GroupId | string)[] = ['/', 'documents', '/cabinets', '/upload', 'esign', 'records', '/approvals', '/reports', 'admin', 'platform'];

export interface RouteDef {
  path: string;
  title: string;
  icon: LucideIcon;
  /** Sidebar placement: a group id, 'top' for a top-level link, or null (not in the menu). */
  nav: GroupId | 'top' | null;
  /** Any of these permissions opens the screen (empty = any signed-in user). */
  perm: string[];
  /** Help slug for the page header link. */
  help?: string;
  /** Match only this exact path for the active menu item (lists with sub-pages below them). */
  end?: boolean;
  /** Count badge in the sidebar (e-Sign: to act / returned). */
  badge?: 'esign-action' | 'esign-returned';
  page: ComponentType | LazyExoticComponent<ComponentType>;
}

const Home = lazy(() => import('./pages/home/HomePage'));
const DocumentsPage = lazy(() => import('./pages/documents/DocumentsPage'));
const CabinetsPage = lazy(() => import('./pages/cabinets/CabinetsPage'));
const UploadPage = lazy(() => import('./pages/upload/UploadPage'));
const InboxPage = lazy(() => import('./pages/inbox/InboxPage'));
const ProcessingPage = lazy(() => import('./pages/processing/ProcessingPage'));
const RulesPage = lazy(() => import('./pages/admin/RulesPage'));
const SearchPage = lazy(() => import('./pages/search/SearchPage'));
// D4
const ViewerPage = lazy(() => import('./pages/viewer/ViewerPage'));
const TrashPage = lazy(() => import('./pages/trash/TrashPage'));
const SharesPage = lazy(() => import('./pages/shares/SharesPage'));
const HelpIndex = lazy(() => import('./help/HelpIndex'));
// D5
const RetentionPage = lazy(() => import('./pages/records/RetentionPage'));
const HoldsPage = lazy(() => import('./pages/records/HoldsPage'));
const DispositionPage = lazy(() => import('./pages/records/DispositionPage'));
const ApprovalsPage = lazy(() => import('./pages/records/ApprovalsPage'));
const NotificationsPage = lazy(() => import('./pages/records/NotificationsPage'));
const ReportsPage = lazy(() => import('./pages/reports/ReportsPage'));
const UsersPage = lazy(() => import('./pages/admin/UsersPage'));
const GroupsPage = lazy(() => import('./pages/admin/GroupsPage'));
const RolesPage = lazy(() => import('./pages/admin/RolesPage'));
const SettingsPage = lazy(() => import('./pages/admin/SettingsPage'));
const ActivityPage = lazy(() => import('./pages/admin/ActivityPage'));
const IntegrityPage = lazy(() => import('./pages/admin/IntegrityPage'));
const ApiKeysPage = lazy(() => import('./pages/admin/ApiKeysPage'));
const ImportPage = lazy(() => import('./pages/admin/ImportPage'));
const SupportAccessPage = lazy(() => import('./pages/admin/SupportAccessPage'));
const OrgsPage = lazy(() => import('./pages/platform/OrganisationsPage'));
const SupportSessionsPage = lazy(() => import('./pages/platform/SupportSessionsPage'));
const QueuePage = lazy(() => import('./pages/platform/QueuePage'));
// e-Sign (ES3)
const EsignInbox = lazy(() => import('./pages/esign/InboxPage'));
const EsignNew = lazy(() => import('./pages/esign/NewRequestPage'));
const EsignRequest = lazy(() => import('./pages/esign/RequestPage'));
const EsignMatrices = lazy(() => import('./pages/esign/MatricesPage'));
const EsignDelegations = lazy(() => import('./pages/esign/DelegationsPage'));
const EsignSettings = lazy(() => import('./pages/esign/EsignSettingsPage'));
// e-Sign (ES4)
const EsignReports = lazy(() => import('./pages/esign/ReportsPage'));


export const ROUTES: RouteDef[] = [
  { path: '/', title: 'Home', icon: House, nav: 'top', perm: [], help: 'getting-started', page: Home },
  { path: '/search', title: 'Search', icon: Search, nav: null, perm: [P.docsView], help: 'finding-documents', page: SearchPage },

  { path: '/documents', title: 'All documents', icon: FileStack, nav: 'documents', perm: [P.docsView], help: 'finding-documents', end: true, page: DocumentsPage },
  { path: '/documents/inbox', title: 'Inbox', icon: Inbox, nav: 'documents', perm: [P.inboxReview], help: 'inbox-rules', page: InboxPage },
  { path: '/documents/recent', title: 'Recently added', icon: Clock3, nav: 'documents', perm: [P.docsView], help: 'finding-documents', page: DocumentsPage },
  { path: '/documents/trash', title: 'Trash', icon: Trash2, nav: 'documents', perm: [P.docsDelete], help: 'versions-trash', page: TrashPage },
  {
    path: '/documents/:id',
    title: 'Document',
    icon: FileText,
    nav: null,
    perm: [P.docsView],
    help: 'viewer',
    page: ViewerPage,
  },

  { path: '/cabinets', title: 'Cabinets', icon: Library, nav: 'top', perm: [P.docsView], help: 'finding-documents', page: CabinetsPage },
  { path: '/cabinets/:cabinetId', title: 'Cabinets', icon: Library, nav: null, perm: [P.docsView], help: 'finding-documents', page: CabinetsPage },
  { path: '/cabinets/:cabinetId/f/:folderId', title: 'Cabinets', icon: Library, nav: null, perm: [P.docsView], help: 'finding-documents', page: CabinetsPage },

  { path: '/upload', title: 'Upload', icon: Upload, nav: 'top', perm: [P.docsUpload], help: 'uploading', page: UploadPage },

  { path: '/esign', title: 'Inbox', icon: PenLine, nav: 'esign', perm: [], help: 'esign-signing', end: true, badge: 'esign-action', page: EsignInbox },
  { path: '/esign/sent', title: 'Sent', icon: Send, nav: 'esign', perm: [P.esignSend], help: 'esign-sending', badge: 'esign-returned', page: EsignInbox },
  { path: '/esign/new', title: 'New request', icon: FilePen, nav: 'esign', perm: [P.esignSend], help: 'esign-sending', page: EsignNew },
  { path: '/esign/matrices', title: 'Templates & matrices', icon: Workflow, nav: 'esign', perm: [P.workflows], help: 'esign-matrices', page: EsignMatrices },
  { path: '/esign/delegations', title: 'Delegations', icon: UserCheck, nav: 'esign', perm: [], help: 'esign-delegation', page: EsignDelegations },
  { path: '/esign/reports', title: 'e-Sign reports', icon: ChartNoAxesColumn, nav: 'esign', perm: [P.reports], help: 'esign-reports', page: EsignReports },
  { path: '/esign/settings', title: 'e-Sign settings', icon: Settings2, nav: 'esign', perm: [P.settings, P.typesManage], help: 'esign-matrices', page: EsignSettings },
  { path: '/esign/requests/:id', title: 'Signing request', icon: PenLine, nav: null, perm: [], help: 'esign-signing', page: EsignRequest },

  {
    path: '/records/retention',
    title: 'Retention',
    icon: Timer,
    nav: 'records',
    perm: [P.retention, P.dispositionPropose],
    end: true,
    help: 'retention',
    page: RetentionPage,
  },
  {
    path: '/records/holds',
    title: 'Legal holds',
    icon: BookLock,
    nav: 'records',
    perm: [P.holds, P.audit],
    end: true,
    help: 'retention',
    page: HoldsPage,
  },
  {
    path: '/records/disposition',
    title: 'Disposition',
    icon: FileX2,
    nav: 'records',
    perm: [P.dispositionPropose, P.dispositionApprove, P.audit],
    end: true,
    help: 'retention',
    page: DispositionPage,
  },

  {
    path: '/approvals',
    title: 'Approvals',
    icon: BadgeCheck,
    nav: 'top',
    perm: [P.approvals, P.dispositionApprove],
    help: 'retention',
    page: ApprovalsPage,
  },
  {
    path: '/reports',
    title: 'Reports',
    icon: FileBarChart,
    nav: 'top',
    perm: [P.reports],
    help: 'reports',
    page: ReportsPage,
  },

  { path: '/admin/users', title: 'Users', icon: Users, nav: 'admin', perm: [P.users], help: 'admin', page: UsersPage },
  { path: '/admin/groups', title: 'Groups', icon: UsersRound, nav: 'admin', perm: [P.groups], help: 'admin', page: GroupsPage },
  { path: '/admin/roles', title: 'Roles', icon: KeyRound, nav: 'admin', perm: [P.roles], help: 'admin', page: RolesPage },
  {
    path: '/admin/cabinets',
    title: 'Cabinets and types',
    icon: SlidersHorizontal,
    nav: 'admin',
    perm: [P.cabinetsManage, P.typesManage],
    help: 'admin',
    page: placeholder({ title: 'Cabinets and types', icon: SlidersHorizontal, sprint: 'D5', help: 'admin', body: 'Cabinets, folders and who may use them; document types with their own fields; tags and correspondents.' }),
  },
  { path: '/admin/rules', title: 'Matching rules', icon: Wand2, nav: 'admin', perm: [P.rulesManage], help: 'inbox-rules', page: RulesPage },
  { path: '/admin/settings', title: 'Settings', icon: Gauge, nav: 'admin', perm: [P.settings], help: 'admin', page: SettingsPage },
  { path: '/admin/activity', title: 'Activity log', icon: History, nav: 'admin', perm: [P.audit], help: 'admin', page: ActivityPage },
  { path: '/admin/shares', title: 'Share links', icon: Link2, nav: 'admin', perm: [P.settings], help: 'download-share', page: SharesPage },

  { path: '/records/holds/:id', title: 'Legal hold', icon: BookLock, nav: null, perm: [P.holds, P.audit, P.retention], help: 'retention', page: HoldsPage },
  { path: '/records/disposition/:id', title: 'Disposal batch', icon: FileX2, nav: null, perm: [P.dispositionPropose, P.dispositionApprove, P.audit], help: 'retention', page: DispositionPage },
  { path: '/admin/integrity', title: 'Integrity checks', icon: ShieldHalf, nav: 'admin', perm: [P.integrity], help: 'admin', end: true, page: IntegrityPage },
  { path: '/admin/integrity/:id', title: 'Integrity check', icon: ShieldHalf, nav: null, perm: [P.integrity], help: 'admin', page: IntegrityPage },
  { path: '/admin/import', title: 'Import', icon: Upload, nav: 'admin', perm: [P.importRun], help: 'admin', page: ImportPage },
  { path: '/admin/api-keys', title: 'API keys', icon: KeyRound, nav: 'admin', perm: [P.apiKeys], help: 'admin', page: ApiKeysPage },

  { path: '/platform/orgs', title: 'Organisations', icon: Building2, nav: 'platform', perm: [P.platformOrgs], help: 'admin', page: OrgsPage },
  { path: '/platform/quotas', title: 'Quotas', icon: Scale, nav: 'platform', perm: [P.platformOrgs], help: 'admin', page: OrgsPage },
  { path: '/platform/support', title: 'Support sessions', icon: LifeBuoy, nav: 'platform', perm: [P.platformSupport], help: 'admin', page: SupportSessionsPage },
  { path: '/platform/queue', title: 'Queue health', icon: Activity, nav: 'platform', perm: [P.platformHealth], help: 'admin', page: QueuePage },

  { path: '/notifications', title: 'Notifications', icon: ListChecks, nav: null, perm: [], page: NotificationsPage },
  { path: '/processing', title: 'Processing', icon: Recycle, nav: null, perm: [P.docsUpload, P.inboxReview], help: 'uploading', page: ProcessingPage },
  { path: '/support', title: 'Support access', icon: ShieldHalf, nav: null, perm: [P.supportApprove], help: 'admin', page: SupportAccessPage },

  { path: '/help', title: 'Help', icon: CircleHelp, nav: 'top', perm: [], page: HelpIndex },
];

/** Routes that sit outside the menu but inside the shell. */
export const EXTRA_ROUTES: { path: string; page: LazyExoticComponent<ComponentType> }[] = [
  { path: '/help/:slug', page: lazy(() => import('./help/HelpArticle')) },
  { path: '/account', page: lazy(() => import('./auth/Account')) },
];

const specificity = (p: string) => p.split('/').reduce((n, s) => n + (s.startsWith(':') ? 1 : 10), 0);

/** The route that best matches `pathname` (static segments beat :params). */
export function routeFor(pathname: string): RouteDef | undefined {
  const parts = pathname.split('/').filter(Boolean);
  const matches = ROUTES.filter((r) => {
    const rp = r.path.split('/').filter(Boolean);
    if (r.path === '/') return pathname === '/';
    if (rp.length > parts.length) return false;
    return rp.every((s, i) => s.startsWith(':') || s === parts[i]);
  });
  return matches.sort((a, b) => specificity(b.path) - specificity(a.path))[0];
}
